import {
	formatToCurrency,
	formatToThousands as formatToCommaSeparated,
} from '../formatting';

export function validateWebsite(
	value: string | undefined,
	emptyAllowed: boolean,
) {
	if (value === undefined && !emptyAllowed) {
		return 'Website cannot be empty';
	}

	if (!emptyAllowed && (!value || value.length < 1 || value.trim() === '')) {
		return 'Website cannot be empty';
	}

	if (value && value.length > 255) {
		return 'Website URL cannot exceed 255 characters';
	}

	if (value && (emptyAllowed ? value.length >= 1 : true)) {
		try {
			// biome-ignore lint/style/noNonNullAssertion: <explanation>
			new URL(value!); // Try to create a URL object
			return undefined; // If successful, the URL is valid
		} catch {
			return 'Invalid website URL format';
		}
	}
}

export function validateEmail(
	value: string | undefined,
	emptyAllowed: boolean,
) {
	if (value === undefined && !emptyAllowed) {
		return 'Email address cannot be empty';
	}

	if (!emptyAllowed && (!value || value.length < 1 || value.trim() === '')) {
		return 'Email address cannot be empty';
	}

	if (value && value.length > 254) {
		return 'Email address cannot exceed 254 characters';
	}

	const emailRegex =
		/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

	if (
		value &&
		(emptyAllowed ? value.length >= 1 : true) &&
		!emailRegex.test(value)
	) {
		return 'Invalid email address format';
	}
}

export function validatePostcode(
	value: string | undefined,
	emptyAllowed: boolean,
) {
	if (value === undefined && !emptyAllowed) {
		return 'Postcode cannot be empty';
	}

	if (!emptyAllowed && (!value || value.length < 1 || value.trim() === '')) {
		return 'Postcode cannot be empty';
	}

	if (value && value.length > 10) {
		return 'Postcode cannot exceed 10 characters';
	}

	const ukPostcodeRegex =
		/^([A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}|[Gg][Ii][Rr] ?0[Aa]{2})$/;

	if (
		(value && emptyAllowed ? value.length >= 1 : true) &&
		value &&
		!ukPostcodeRegex.test(value)
	) {
		return 'Invalid UK postcode format';
	}
}

export function validateAddressLine(
	value: string | undefined,
	emptyAllowed: boolean,
) {
	if (value === undefined && !emptyAllowed) {
		return 'Address line cannot be empty';
	}

	if (
		!emptyAllowed &&
		(!value || value === null || value.length < 1 || value.trim() === '')
	) {
		return 'Address line cannot be empty';
	}
	if (value && value.length > 120) {
		return 'Address line cannot exceed 120 characters';
	}
	if (
		(value && emptyAllowed ? value.length >= 1 : true) &&
		value &&
		!/^[a-zA-Z0-9. _']{1,}$/.test(value)
	) {
		return 'Address line cannot be empty and can only contain apostrophes, letters, numbers, dots or spaces ';
	}
}

export function validateText(
	value: string | undefined,
	emptyAllowed: boolean,
	propertyDescription?: string,
	maxLength?: number,
) {
	if (
		(emptyAllowed && (value === null || value === undefined)) ||
		// biome-ignore lint/style/noNonNullAssertion: <explanation>
		(emptyAllowed && value!.length < 1)
	) {
		return; // Valid case, no error
	}

	if (
		!emptyAllowed &&
		(!value ||
			value === null ||
			value === undefined ||
			value.length < 1 ||
			value.trim() === '')
	) {
		return `${propertyDescription ?? 'Name'} cannot be empty`;
	}
	// biome-ignore lint/style/noNonNullAssertion: <explanation>
	if (value!.length > (maxLength ?? 70)) {
		return `${propertyDescription ?? 'Name'} cannot exceed ${maxLength ?? 70} characters`;
	}
	if (
		// biome-ignore lint/style/noNonNullAssertion: <explanation>
		(emptyAllowed ? value!.length >= 1 : true) &&
		// biome-ignore lint/style/noNonNullAssertion: <explanation>
		!/^[a-zA-Z0-9.\- _'&]{1,}$/.test(value!)
	) {
		return `${propertyDescription ?? 'Name'} can only contain: ampersands, apostrophes, letters, numbers, dots or spaces `;
	}
}

export function validatePhoneNumber(
	value: string | undefined,
	emptyAllowed: boolean,
) {
	if (!emptyAllowed && value === undefined) {
		return 'Phone number cannot be empty';
	}

	if (!emptyAllowed && (!value || value.length < 1 || value.trim() === '')) {
		return 'Phone number cannot be empty';
	}
	if (
		value &&
		(emptyAllowed ? value.length >= 1 : true) &&
		!/^\d{5}\s\d{6}$/.test(value)
	) {
		return 'Phone number is invalid';
	}
}

export function validateNumber(
	value: string | undefined,
	emptyAllowed: boolean,
	min: number,
	max: number,
	propertyDescription?: string,
	isFinance = true,
) {
	if ((emptyAllowed && value === undefined) || value === null) {
		return;
	}

	const valueAsNumber = Number(value);

	if ((!emptyAllowed && value === undefined) || value === null) {
		return `${propertyDescription ?? 'Number'} can not be undefined `;
	}

	// biome-ignore lint/style/noNonNullAssertion: <explanation>
	if (!emptyAllowed && value!.length < 1) {
		return `${propertyDescription ?? 'Number'} can not be empty `;
	}

	if (
		// biome-ignore lint/style/noNonNullAssertion: <explanation>
		(emptyAllowed ? value!.length >= 1 : true) &&
		Number.isNaN(valueAsNumber)
	) {
		return `${propertyDescription ?? 'Number'} is malformed `;
	}

	// biome-ignore lint/style/noNonNullAssertion: <explanation>
	if ((emptyAllowed ? value!.length >= 1 : true) && valueAsNumber > max) {
		return `${propertyDescription ?? 'Number'} can not be greater than ${isFinance ? formatToCurrency(max) : formatToCommaSeparated(max)} `;
	}

	// biome-ignore lint/style/noNonNullAssertion: <explanation>
	if ((emptyAllowed ? value!.length >= 1 : true) && valueAsNumber < min) {
		return `${propertyDescription ?? 'Number'} can not be smaller than ${isFinance ? formatToCurrency(min) : formatToCommaSeparated(min)} `;
	}
}

export function validateDate(
	value: Date | undefined,
	emptyAllowed: boolean,
	propertyDescription?: string,
) {
	if (!emptyAllowed) {
		if (!value) {
			return `${propertyDescription ?? 'Date/time'} cannot be empty `;
		}
	}
}

export function validateEndDate(
	value: Date | undefined,
	emptyAllowed: boolean,
	startDate?: Date | undefined,
	propertyDescription?: string,
) {
	if (!emptyAllowed) {
		if (!value) {
			return `${propertyDescription ?? 'Date/time'} cannot be empty `;
		}
		if (startDate && startDate > value) {
			return `${propertyDescription ?? 'End date/time'} cannot be before start date/time`;
		}
	}
}

export function validateNotEmpty(
	value: string | undefined,
	propertyDescription?: string,
) {
	if (!value) {
		return `${propertyDescription ?? 'Property'} cannot be empty `;
	}
}

export function validatePassword(value: string, emptyAllowed: boolean) {
	if (
		(emptyAllowed && (value === null || value === undefined)) ||
		// biome-ignore lint/style/noNonNullAssertion: <explanation>
		(emptyAllowed && value!.length < 1)
	) {
		return; // Valid case, no error
	}

	if (!/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&^_-]{8,}$/.test(value)) {
		return 'Passwords must contain minimum eight characters, at least one letter and one number';
	}
	if (value.length > 100) {
		return 'Password cannot exceed 99 characters';
	}
}

export function validateUsername(value: string) {
	if (value.length < 6) {
		return 'Username must be at least 6 characters long';
	}
	if (value.length > 30) {
		return 'Username cannot exceed 30 characters';
	}
	if (!/^[a-zA-Z0-9._]{1,}$/.test(value)) {
		return "Username can only include letters, numbers, underscores and '.'";
	}
}
